import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  viewChildren
} from '@angular/core'
import { NzModalModule } from 'ng-zorro-antd/modal'
import { LogrocketPFFacade } from 'projects/aiomed/src/shared/services/logrocket-pf-facade.service'
import { LOGROCKET_LOG_TECH_MODE_CODE } from 'projects/aiomed/src/shared/constants/logrocket-log-keys'
import {
  PIN_CODE_ABSCISSA_REQUIRED_VALUE,
  PIN_CODE_ITEMS_LENGTH,
  PIN_CODE_ORDINATE_REQUIRED_VALUE,
  PIN_CODE_REQUIRED_DURATION_MILLISECONDS,
  PIN_CODE_VALUE
} from '../../pages/auth/constants/pin-code-value'

@Component({
  selector: 'aiomed-blocking-modal',
  imports: [NzModalModule],
  templateUrl: './blocking-modal.component.html',
  styleUrl: './blocking-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.has-virtual-keyboard]': 'hasVirtualKeyboard()',
    '[class.has-focus]': 'hasFocus()'
  }
})
export class BlockingModalComponent {
  private readonly logrocketPfFacade = inject(LogrocketPFFacade)

  readonly pinCodeInputs =
    viewChildren<ElementRef<HTMLInputElement>>('pinCodeInput')

  readonly pinCodeCorrectlyEntered = output<void>()

  readonly hasVirtualKeyboard = input.required<boolean>()
  readonly isVisible = input.required<boolean>()

  protected readonly isPinCodeShown = signal<boolean>(false)
  protected readonly pinCodeItemsFromLength = computed<string[]>(() => {
    const length = this.pinCodeItemsLength()
    return Array(length).fill('')
  })
  protected readonly pinCodeValue = linkedSignal({
    source: () => this.pinCodeItemsFromLength(),
    computation: items => items
  })
  protected readonly pinCodeError = linkedSignal({
    source: () => this.pinCodeValue(),
    computation: () => false
  })

  protected readonly hasFocus = signal<boolean>(false)

  private readonly pinCodeItemsLength = signal<number>(PIN_CODE_ITEMS_LENGTH)
  private temporaryValue: string = ''
  private defaultTouches = { x: 0, y: 0, time: 0 }

  constructor() {
    effect(() => {
      this.focusPinCodeFieldIfVisible()
    })
  }

  public confirmPinCode(): void {
    const value = this.pinCodeValue().join('')
    if (value !== PIN_CODE_VALUE) {
      this.pinCodeError.set(true)
      return
    }
    this.pinCodeCorrectlyEntered.emit()
  }

  public togglePinCodeVisibility(): void {
    const isPinCodeShown = this.isPinCodeShown()
    this.isPinCodeShown.set(!isPinCodeShown)
  }

  public removePinCodeValue(currentIndex: number): void {
    this.hasFocus.set(true)

    this.pinCodeValue.update(pinCodeValue =>
      pinCodeValue.map((existingValue, index) => {
        if (index !== currentIndex) {
          return existingValue
        }
        this.temporaryValue = existingValue
        return ''
      })
    )
  }

  public setPinCodeValue(value: string, currentIndex: number): void {
    this.temporaryValue = ''
    this.pinCodeValue.update(pinCodeValue =>
      pinCodeValue.map((existingValue, index) =>
        index !== currentIndex
          ? existingValue
          : value === ''
            ? ''
            : value[value.length - 1]
      )
    )

    const pinCodeInputs = this.pinCodeInputs()
    if (!pinCodeInputs) {
      return
    }
    if (value === '' && currentIndex !== 0) {
      pinCodeInputs[currentIndex - 1].nativeElement.focus()
      return
    }
    if (currentIndex >= pinCodeInputs.length - 1) {
      this.confirmPinCode()
      return
    }
    pinCodeInputs[currentIndex + 1].nativeElement.focus()
  }

  public returnPinCodeValue(event: Event, currentIndex: number): void {
    this.hasFocus.set(false)

    if (!this.temporaryValue) {
      return
    }
    this.pinCodeValue.update(pinCodeValue =>
      pinCodeValue.map((existingValue, index) =>
        index !== currentIndex ? existingValue : this.temporaryValue
      )
    )
    this.temporaryValue = ''
  }

  public handleBackspaceKeyDown(
    event: KeyboardEvent,
    currentIndex: number
  ): void {
    if (event.key !== 'Backspace') {
      return
    }
    this.temporaryValue = ''
    this.pinCodeValue.update(pinCodeValue =>
      pinCodeValue.map((existingValue, index) =>
        index !== currentIndex ? existingValue : ''
      )
    )
    const pinCodeInputs = this.pinCodeInputs()
    if (!pinCodeInputs) {
      return
    }
    if (currentIndex === 0) {
      return
    }
    pinCodeInputs[currentIndex - 1].nativeElement.focus()
  }

  private focusPinCodeFieldIfVisible(): void {
    const pinCodeInputs = this.pinCodeInputs()
    if (!pinCodeInputs) {
      return
    }

    pinCodeInputs[0].nativeElement.focus()
  }

  protected handleTouchToShowPinCode(event: TouchEvent): void {
    let touch = event.touches[0] || event.changedTouches[0]
    if (event.type === 'touchstart') {
      this.defaultTouches.x = touch.pageX
      this.defaultTouches.y = touch.pageY
      this.defaultTouches.time = event.timeStamp
    } else if (event.type === 'touchend') {
      let deltaX = touch.pageX - this.defaultTouches.x
      let deltaY = touch.pageY - this.defaultTouches.y
      let deltaTime = event.timeStamp - this.defaultTouches.time

      if (deltaTime < PIN_CODE_REQUIRED_DURATION_MILLISECONDS) {
        return
      }
      if (deltaX <= PIN_CODE_ABSCISSA_REQUIRED_VALUE) {
        return
      }
      if (deltaY <= PIN_CODE_ORDINATE_REQUIRED_VALUE) {
        return
      }

      this.togglePinCodeVisibility()
      this.logrocketPfFacade.logrocketTrack(LOGROCKET_LOG_TECH_MODE_CODE, {
        time: new Date().toISOString()
      })
    }
  }
}
